import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DashboardWidget, DashboardLayout } from '../types/dashboard-widgets';

interface DashboardStore {
  // State
  layout: DashboardLayout;
  isEditMode: boolean;
  draggedWidgetId: string | null;
  
  // Actions
  toggleEditMode: () => void;
  setDraggedWidget: (id: string | null) => void;
  
  // Widget CRUD
  addWidget: (widget: Omit<DashboardWidget, 'id' | 'order'>) => void;
  updateWidget: (id: string, updates: Partial<DashboardWidget>) => void;
  removeWidget: (id: string) => void;
  reorderWidgets: (orderedIds: string[]) => void;
  
  // Layout
  resetLayout: () => void;
}

const generateId = () => `w_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

// Layout par défaut - Trésorerie et Cote Crédit uniquement
const createDefaultLayout = (): DashboardLayout => ({
  id: 'default',
  name: 'Tableau de bord principal',
  widgets: [
    // Widget 1: Cote de Crédit Wanzo (donnée principale!)
    {
      id: generateId(),
      type: 'credit_score',
      title: 'Cote de Crédit Wanzo',
      size: 'md',
      config: {},
      order: 0
    },
    // Widget 2: Trésorerie détaillée
    {
      id: generateId(),
      type: 'cash',
      title: 'Trésorerie',
      size: 'md',
      config: {},
      order: 1
    }
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
});

export const useDashboardStore = create<DashboardStore>()(
  persist(
    (set) => ({
      layout: createDefaultLayout(),
      isEditMode: false,
      draggedWidgetId: null,

      toggleEditMode: () => set(state => ({ isEditMode: !state.isEditMode })),
      
      setDraggedWidget: (id) => set({ draggedWidgetId: id }),

      addWidget: (widget) => set(state => {
        const maxOrder = Math.max(...state.layout.widgets.map(w => w.order ?? 0), -1);
        const newWidget: DashboardWidget = {
          ...widget,
          id: generateId(),
          order: maxOrder + 1
        };
        return {
          layout: {
            ...state.layout,
            widgets: [...state.layout.widgets, newWidget],
            updatedAt: new Date().toISOString()
          }
        };
      }),

      updateWidget: (id, updates) => set(state => ({
        layout: {
          ...state.layout,
          widgets: state.layout.widgets.map(w =>
            w.id === id ? { ...w, ...updates } : w
          ),
          updatedAt: new Date().toISOString()
        }
      })),

      removeWidget: (id) => set(state => ({
        layout: {
          ...state.layout,
          widgets: state.layout.widgets.filter(w => w.id !== id),
          updatedAt: new Date().toISOString()
        }
      })),

      reorderWidgets: (orderedIds) => set(state => ({
        layout: {
          ...state.layout,
          widgets: orderedIds
            .map((id, index) => {
              const widget = state.layout.widgets.find(w => w.id === id);
              return widget ? { ...widget, order: index } : null;
            })
            .filter((w): w is NonNullable<typeof w> => w !== null),
          updatedAt: new Date().toISOString()
        }
      })),

      resetLayout: () => set({ layout: createDefaultLayout() })
    }),
    {
      name: 'wanzo-dashboard-layout',
      version: 3
    }
  )
);
